import { useEffect } from 'react'
import { ArrayParam, StringParam, useQueryParams } from 'use-query-params'

import { useBreadcrumbsData } from '@percent/admin-dashboard/app/Layout/breadcrumbsContext/useBreadcrumbsData/useBreadcrumbsData'
import { ErrorView, Loader } from '@percent/admin-dashboard/common/components'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { DisbursementStatus } from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { useQuery, useQueryList } from '@percent/utility'
import { DisbursementsBreakdown } from './disbursementsBreakdown/DisbursementsBreakdown'
import { DisbursementFoundationDetails } from './DisbursementFoundationDetails/DisbursementFoundationDetails'
import { Flex } from '@percent/lemonade'
import { PaymentInProgress } from './paymentInProgress/PaymentInProgress'
import { StatusOverview } from './statusOverview/StatusOverview'
import { DisbursementStatusAndTotal, DisbursementSummaryRow } from './statusOverview/StatusOverview.types'
import { DisbursementsBreakdownQueryParams } from './disbursementsBreakdown/disbursementsBreakdownFilters/DisbursementsBreakdownFilters.types'

export function DisbursementDetailsContainer() {
  const { id } = useRequiredParams()
  const { disbursementsService } = useServices()
  const logReturnedPaymentFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_LOG_RETURNED_PAYMENT)
  const { setPath } = useBreadcrumbsData()

  const [queryParams, setQueryParamsRaw] = useQueryParams({
    organisationId: ArrayParam,
    status: ArrayParam,
    beneficiaryStatus: ArrayParam,
    sort: StringParam,
    direction: StringParam
  })

  const setQueryParams = (params: DisbursementsBreakdownQueryParams) => {
    setQueryParamsRaw({
      organisationId: params.organisationId ?? [],
      status: params.status ?? [],
      beneficiaryStatus: params.beneficiaryStatus ?? [],
      sort: params.sort ?? null,
      direction: params.direction ?? null
    })
  }

  const filteredOrganisationId = queryParams.organisationId?.filter(
    (orgId): orgId is string => orgId !== null && orgId !== undefined
  )

  const filteredStatus = queryParams.status?.filter(
    (status): status is string => status !== null && status !== undefined
  )

  const filteredBeneficiaryStatus = queryParams.beneficiaryStatus?.filter(
    (value): value is string => value !== null && value !== undefined
  )

  const {
    data: disbursementBatchDetails,
    errorMessage: disbursementBatchDetailsErrorMessage,
    isLoading: isDisbursementBatchDetailsLoading,
    refetch: refreshDisbursementBatchDetails
  } = useQuery(['disbursement-batch-details', id], disbursementsService.getDisbursementBatchDetails, { id })

  // Show the batch name in the breadcrumb when one is set; resolveBreadcrumbs falls back to the id otherwise.
  useEffect(() => {
    setPath(disbursementBatchDetails?.name)

    return () => setPath(undefined)
  }, [setPath, disbursementBatchDetails?.name])

  const sort = queryParams.sort ?? undefined
  const direction = queryParams.direction ?? undefined

  const [
    {
      errorMessage: disbursementsErrorMessage,
      isLoading: isDisbursementsLoading,
      data: disbursements,
      totalResults,
      error: disbursementsError
    },
    { nextPage, previousPage, refresh: refreshDisbursements }
  ] = useQueryList(
    [
      'disbursement-batch-enhanced-disbursements',
      id,
      filteredOrganisationId,
      filteredStatus,
      filteredBeneficiaryStatus,
      sort,
      direction
    ],
    disbursementsService.getDisbursementBatchEnhancedDisbursementsList(id),
    {
      pageSize: 100,
      organisationId: filteredOrganisationId?.length ? filteredOrganisationId : undefined,
      status: filteredStatus?.length ? filteredStatus : undefined,
      beneficiaryStatus: filteredBeneficiaryStatus?.length ? filteredBeneficiaryStatus : undefined,
      sort,
      direction
    }
  )

  if (disbursementsErrorMessage && disbursementsError) {
    return <ErrorView errorMessage={disbursementsErrorMessage} />
  }

  if (disbursementBatchDetailsErrorMessage) {
    return <ErrorView errorMessage={disbursementBatchDetailsErrorMessage} />
  }

  if (isDisbursementBatchDetailsLoading || isDisbursementsLoading) {
    return <Loader />
  }

  if (!disbursementBatchDetails) {
    return <div>No disbursement batch details</div>
  }

  const { counts, totals, detailedCounts, endDate, foundationId, allBeneficiariesConfirmed } = disbursementBatchDetails

  const pendingApprovalCount =
    counts.approvalRequired +
    counts.trusteeApprovalRequired +
    counts.agentApprovalRequired +
    counts.managerApprovalRequired

  const sortedCounts = {
    total: counts.total,
    approvalRequired: pendingApprovalCount,
    approved: counts.approved,
    inTransit: counts.inTransit + (counts.paymentInitiated ?? 0),
    paid: counts.paid,
    ...(logReturnedPaymentFeatureFlag ? { returned: counts.returned ?? 0 } : {}),
    failed: counts.failed,
    cancelled: counts.cancelled
  }

  const summary: DisbursementSummaryRow[] = Object.entries(sortedCounts).map(([status, count]) => ({
    status: status as DisbursementStatusAndTotal,
    count: count as number
  }))

  const refreshData = () => {
    refreshDisbursementBatchDetails()
    refreshDisbursements()
  }

  return (
    <Flex direction="column" gap={16}>
      <DisbursementFoundationDetails disbursementBatchId={id} refreshData={refreshData} {...disbursementBatchDetails} />
      <StatusOverview counts={counts} totals={totals} showReturned={logReturnedPaymentFeatureFlag} />
      <PaymentInProgress
        disbursementBatchId={id}
        counts={counts}
        foundationId={foundationId}
        detailedCounts={detailedCounts}
        onProgressChange={refreshData}
        onFilterFailedPayments={() =>
          setQueryParams({
            organisationId: filteredOrganisationId,
            status: [DisbursementStatus.FAILED],
            beneficiaryStatus: filteredBeneficiaryStatus,
            sort,
            direction
          })
        }
      />
      <DisbursementsBreakdown
        disbursementBatchId={id}
        data={disbursements ?? []}
        totalResults={totalResults}
        previousPage={previousPage}
        nextPage={nextPage}
        refreshData={refreshData}
        queryParams={{
          organisationId: filteredOrganisationId,
          status: filteredStatus,
          beneficiaryStatus: filteredBeneficiaryStatus,
          sort,
          direction
        }}
        setQueryParams={setQueryParams}
        foundationId={foundationId}
        endDate={endDate}
        detailedCounts={detailedCounts}
        summary={summary}
        totals={totals}
        totalDisbursementsCount={counts.managerApprovalRequired}
        allBeneficiariesConfirmed={allBeneficiariesConfirmed}
      />
    </Flex>
  )
}
